import {
  addDays,
  addMilliseconds,
  differenceInCalendarDays,
  differenceInMinutes,
  eachDayOfInterval,
  isSameDay,
  max as maxDate,
  min as minDate,
  startOfDay,
} from "date-fns";

import {
  type CalendarBusinessHours,
  type CalendarEvent,
  type CalendarRange,
} from "./types";
import { getDayKey, getInclusiveEnd } from "./utils";

// =============================================================================
// Pure layout math shared by the week and month views. No React here.
// =============================================================================

/** Event contained in a single calendar day (timed or all-day) */
export function isSingleDay(event: CalendarEvent): boolean {
  return isSameDay(event.start, getInclusiveEnd(event));
}

/**
 * Wall-clock minutes of `date` counted from the midnight of `day`: negative
 * before that day, 1440 and more after it. Unlike elapsed milliseconds, this
 * keeps 10:00 at 10:00 on the days the clocks change.
 */
function wallMinutes(date: Date, day: Date): number {
  return (
    differenceInCalendarDays(date, day) * 1440 +
    date.getHours() * 60 +
    date.getMinutes()
  );
}

// =============================================================================
// Event index
// =============================================================================

export type EventIndex = {
  /** Timed events intersecting each day, sorted (start asc, longer first) */
  timedByDay: Map<string, CalendarEvent[]>;
  /** All-day events intersecting each day, same order */
  allDayByDay: Map<string, CalendarEvent[]>;
};

function byStartThenLength(a: CalendarEvent, b: CalendarEvent): number {
  return (
    a.start.getTime() - b.start.getTime() ||
    b.end.getTime() - b.start.getTime() - (a.end.getTime() - a.start.getTime())
  );
}

/**
 * Day-keyed index of the events intersecting `range`. Multi-day events are
 * listed on every covered day of the range, so a decade-long event costs no
 * more than the days on screen.
 */
export function buildEventIndex(
  events: CalendarEvent[],
  range: CalendarRange
): EventIndex {
  const timedByDay = new Map<string, CalendarEvent[]>();
  const allDayByDay = new Map<string, CalendarEvent[]>();
  const firstVisibleDay = startOfDay(range.start);
  const lastVisibleDay = startOfDay(addMilliseconds(range.end, -1));

  for (const event of events) {
    const inclusiveEnd = getInclusiveEnd(event);
    if (event.start >= range.end || inclusiveEnd < range.start) continue;
    const target = event.allDay ? allDayByDay : timedByDay;
    const days = eachDayOfInterval({
      start: maxDate([startOfDay(event.start), firstVisibleDay]),
      end: minDate([startOfDay(inclusiveEnd), lastVisibleDay]),
    });
    for (const day of days) {
      const key = getDayKey(day);
      const list = target.get(key);
      if (list) list.push(event);
      else target.set(key, [event]);
    }
  }

  for (const list of timedByDay.values()) list.sort(byStartThenLength);
  for (const list of allDayByDay.values()) list.sort(byStartThenLength);
  return { timedByDay, allDayByDay };
}

// =============================================================================
// Disabled ranges and free slots (week view)
// =============================================================================

/** A disabled range clipped to one day, in minutes from midnight */
export type DisabledSpan = { startMin: number; endMin: number };

/** Disabled spans intersecting `day`, clipped to the visible hours */
export function getDisabledSpansForDay(
  day: Date,
  ranges: CalendarRange[],
  minHour: number,
  maxHour: number
): DisabledSpan[] {
  const spans: DisabledSpan[] = [];
  for (const range of ranges) {
    const startMin = Math.max(wallMinutes(range.start, day), minHour * 60);
    const endMin = Math.min(wallMinutes(range.end, day), maxHour * 60);
    if (endMin > startMin) spans.push({ startMin, endMin });
  }
  return spans;
}

/** True when [startMin, endMin) intersects one of the spans */
export function isSpanBlocked(
  startMin: number,
  endMin: number,
  spans: DisabledSpan[]
): boolean {
  return spans.some((span) => startMin < span.endMin && span.startMin < endMin);
}

/** True when a single range covers the whole day */
export function isDayFullyDisabled(
  day: Date,
  ranges: CalendarRange[]
): boolean {
  const dayStart = startOfDay(day);
  const nextDay = addDays(dayStart, 1);
  return ranges.some(
    (range) => range.start <= dayStart && range.end >= nextDay
  );
}

/**
 * Start minutes of the free slots of `day`: inside the visible hours, outside
 * every disabled span and not overlapped by a timed event. All-day events do
 * not block anything (use `disabledRanges` for closed days).
 */
export function getFreeSlots(
  day: Date,
  events: CalendarEvent[],
  spans: DisabledSpan[],
  minHour: number,
  maxHour: number,
  slotDuration: number
): number[] {
  const busy = events
    .filter((event) => !event.allDay)
    .map((event) => {
      const startMin = wallMinutes(event.start, day);
      // A zero-length event still holds the slot it sits in
      return {
        startMin,
        endMin: Math.max(wallMinutes(event.end, day), startMin + 1),
      };
    });

  const slots: number[] = [];
  for (let start = minHour * 60; start < maxHour * 60; start += slotDuration) {
    const end = start + slotDuration;
    if (isSpanBlocked(start, end, spans) || isSpanBlocked(start, end, busy)) {
      continue;
    }
    slots.push(start);
  }
  return slots;
}

// =============================================================================
// Week view: timed events of a day column
// =============================================================================

/** Geometry in CSS percentages of the day column */
export type PositionedEvent = {
  event: CalendarEvent;
  top: number;
  height: number;
  left: number;
  width: number;
  /** False on the segments of a multi-day event started on a previous day */
  isStart: boolean;
  /** False on the segments of a multi-day event continuing past this day */
  isEnd: boolean;
};

/**
 * Positions the timed events of one day inside the hour grid. Multi-day
 * events are clipped to the day (one segment per covered day); with business
 * hours, their clipped midnight edges are clamped to the working window.
 *
 * Classic 3-pass algorithm: sort (start asc, longer first), group into
 * clusters of transitively overlapping events, then give each event the first
 * free column of its cluster. Events then widen to the right up to the first
 * column holding an event they overlap.
 */
export function layoutDayEvents(
  day: Date,
  events: CalendarEvent[],
  minHour: number,
  maxHour: number,
  minEventMinutes: number,
  businessHours?: CalendarBusinessHours
): PositionedEvent[] {
  const gridStart = minHour * 60;
  const gridEnd = maxHour * 60;
  const totalMinutes = gridEnd - gridStart;

  type Item = {
    event: CalendarEvent;
    startMin: number;
    endMin: number;
    isStart: boolean;
    isEnd: boolean;
  };
  const items: Item[] = [];
  for (const event of events) {
    let startMin = wallMinutes(event.start, day);
    let endMin = Math.max(wallMinutes(event.end, day), startMin);
    const isStart = isSameDay(event.start, day);
    const isEnd = isSameDay(getInclusiveEnd(event), day);
    // Overnight edges clamped to the working window (purely visual), unless
    // the segment would vanish
    if (businessHours && (!isStart || !isEnd)) {
      const clampedStart = isStart
        ? startMin
        : Math.max(startMin, businessHours.startHour * 60);
      const clampedEnd = isEnd
        ? endMin
        : Math.min(endMin, businessHours.endHour * 60);
      if (clampedEnd > clampedStart) {
        startMin = clampedStart;
        endMin = clampedEnd;
      }
    }
    // Visual minimum so very short events stay readable and clickable
    endMin = Math.max(endMin, startMin + minEventMinutes);
    startMin = Math.max(startMin, gridStart);
    endMin = Math.min(endMin, gridEnd);
    if (endMin <= startMin) continue;
    items.push({ event, startMin, endMin, isStart, isEnd });
  }

  items.sort((a, b) => a.startMin - b.startMin || b.endMin - a.endMin);

  const positioned: PositionedEvent[] = [];
  const flushCluster = (cluster: Item[]) => {
    // Greedy column assignment: first column whose last event already ended
    const columnEnds: number[] = [];
    const columns = cluster.map((item) => {
      let column = columnEnds.findIndex((end) => end <= item.startMin);
      if (column === -1) column = columnEnds.length;
      columnEnds[column] = item.endMin;
      return column;
    });
    const columnWidth = 100 / columnEnds.length;
    cluster.forEach((item, i) => {
      let limit = columnEnds.length;
      cluster.forEach((other, j) => {
        if (
          columns[j] > columns[i] &&
          columns[j] < limit &&
          item.startMin < other.endMin &&
          other.startMin < item.endMin
        ) {
          limit = columns[j];
        }
      });
      positioned.push({
        event: item.event,
        top: ((item.startMin - gridStart) / totalMinutes) * 100,
        height: ((item.endMin - item.startMin) / totalMinutes) * 100,
        left: columns[i] * columnWidth,
        width: (limit - columns[i]) * columnWidth,
        isStart: item.isStart,
        isEnd: item.isEnd,
      });
    });
  };

  let cluster: Item[] = [];
  let clusterEnd = -Infinity;
  for (const item of items) {
    if (cluster.length && item.startMin >= clusterEnd) {
      flushCluster(cluster);
      cluster = [];
      clusterEnd = -Infinity;
    }
    cluster.push(item);
    clusterEnd = Math.max(clusterEnd, item.endMin);
  }
  if (cluster.length) flushCluster(cluster);

  return positioned;
}

// =============================================================================
// Rows of days: month weeks and the all-day strip
// =============================================================================

export type WeekSegment = {
  event: CalendarEvent;
  /** Columns covered within the row, 0-based inclusive */
  startCol: number;
  endCol: number;
  lane: number;
  /** False when the event started before this row (no left rounding) */
  isStart: boolean;
  /** False when the event continues past this row (no right rounding) */
  isEnd: boolean;
};

/**
 * Lays out the events intersecting a row of days into horizontal lanes:
 * leftmost first, then wider first, so long bars keep the top lanes from row
 * to row. The days may skip hidden weekdays: a bar then spans continuously
 * across them, and an event covering only hidden days is skipped.
 */
export function layoutWeekSegments(
  days: Date[],
  events: CalendarEvent[]
): WeekSegment[] {
  const dayMs = days.map((day) => startOfDay(day).getTime());

  const raw: Omit<WeekSegment, "lane">[] = [];
  for (const event of events) {
    const firstDayMs = startOfDay(event.start).getTime();
    const lastDayMs = startOfDay(getInclusiveEnd(event)).getTime();
    let startCol = -1;
    let endCol = -1;
    dayMs.forEach((ms, col) => {
      if (ms < firstDayMs || ms > lastDayMs) return;
      if (startCol === -1) startCol = col;
      endCol = col;
    });
    if (startCol === -1) continue;
    raw.push({
      event,
      startCol,
      endCol,
      isStart: dayMs[startCol] === firstDayMs,
      isEnd: dayMs[endCol] === lastDayMs,
    });
  }

  raw.sort(
    (a, b) =>
      a.startCol - b.startCol ||
      b.endCol - b.startCol - (a.endCol - a.startCol) ||
      a.event.start.getTime() - b.event.start.getTime() ||
      a.event.id.localeCompare(b.event.id)
  );

  // Greedy packing: first lane whose columns are all free
  const lanes: boolean[][] = [];
  return raw.map((segment) => {
    let lane = 0;
    for (; ; lane++) {
      lanes[lane] ??= new Array<boolean>(days.length).fill(false);
      const row = lanes[lane];
      let free = true;
      for (let col = segment.startCol; col <= segment.endCol; col++) {
        if (row[col]) {
          free = false;
          break;
        }
      }
      if (free) {
        row.fill(true, segment.startCol, segment.endCol + 1);
        break;
      }
    }
    return { ...segment, lane };
  });
}

/** A single-day event with its duration share of the cell height */
export type DayBlock = {
  event: CalendarEvent;
  /** Flex-grow weight: the longer the event, the taller the block */
  weight: number;
};

/**
 * Single-day events of each column, sorted by start and weighted by their
 * duration: the blocks of a cell split its height proportionally (a lone event
 * fills the cell). All-day events weigh a whole window. Multi-day events are
 * left out: they render as bars.
 */
export function layoutDayBlocks(
  days: Date[],
  events: CalendarEvent[],
  windowMinutes: number
): DayBlock[][] {
  const singleDay = events.filter(isSingleDay);
  return days.map((day) =>
    singleDay
      .filter((event) => isSameDay(event.start, day))
      .sort((a, b) => a.start.getTime() - b.start.getTime())
      .map((event) => ({
        event,
        weight: event.allDay
          ? windowMinutes
          : Math.max(differenceInMinutes(event.end, event.start), 30),
      }))
  );
}

export type MonthRowLayout = {
  /** Visible multi-day bars */
  bars: WeekSegment[];
  /** Bar lanes crossing each column: how far down its blocks start */
  barLanesByCol: number[];
  /** Visible single-day blocks of each column */
  blocksByCol: DayBlock[][];
  /** Events of each column that did not fit, behind "+N more" */
  hiddenByCol: CalendarEvent[][];
  /** Lanes the tallest column needs, its "+N more" button included */
  laneCount: number;
};

/**
 * Layout of a month week row: multi-day events are continuous bars in the top
 * lanes, single-day events are blocks sharing the rest of their cell, capped
 * at `maxLanes` items per column. The overflow is listed per column for the
 * "+N more" button, which takes one more lane in the columns that need it.
 */
export function layoutMonthRow(
  week: Date[],
  events: CalendarEvent[],
  maxLanes: number,
  windowMinutes: number
): MonthRowLayout {
  const hiddenByCol: CalendarEvent[][] = week.map(() => []);

  const segments = layoutWeekSegments(
    week,
    events.filter((event) => !isSingleDay(event))
  );
  const bars: WeekSegment[] = [];
  const barLanesByCol = new Array<number>(week.length).fill(0);
  for (const segment of segments) {
    for (let col = segment.startCol; col <= segment.endCol; col++) {
      if (segment.lane < maxLanes) {
        barLanesByCol[col] = Math.max(barLanesByCol[col], segment.lane + 1);
      } else {
        hiddenByCol[col].push(segment.event);
      }
    }
    if (segment.lane < maxLanes) bars.push(segment);
  }

  const blocksByCol = layoutDayBlocks(week, events, windowMinutes).map(
    (blocks, col) => {
      const budget = Math.max(0, maxLanes - barLanesByCol[col]);
      for (const block of blocks.slice(budget)) {
        hiddenByCol[col].push(block.event);
      }
      return blocks.slice(0, budget);
    }
  );

  const laneCount = Math.max(
    0,
    ...week.map(
      (_, col) =>
        barLanesByCol[col] +
        blocksByCol[col].length +
        (hiddenByCol[col].length > 0 ? 1 : 0)
    )
  );

  return { bars, barLanesByCol, blocksByCol, hiddenByCol, laneCount };
}
